import { useState } from "react";
import { Link } from "react-router";
import { TournamentPicker } from "../../components/TournamentPicker";
import { ApiError } from "../../lib/api";
import { effectiveTournamentStatus, tournamentInCurrentWeek } from "../../lib/tournamentTimeline";
import { NewTournamentDialog } from "./NewTournamentDialog";
import { TournamentDetailsEditor } from "./TournamentDetailsEditor";
import { TournamentOperationsPanel } from "./TournamentOperationsPanel";
import { useTournamentPreparation } from "./useTournamentPreparation";
import "./preparation.css";
import "./preparation-edit.css";
import "./tournament-details.css";
import "./tournament-catalog.css";

function dateRange(start: string, end: string) {
  if (!start && !end) return "Termin nieustalony";
  if (!end || start === end) return start || end;
  return `${start} — ${end}`;
}

function statusLabel(key: "planned" | "active" | "completed") {
  if (key === "active") return "Aktywny";
  if (key === "completed") return "Zakończony";
  return "Planowany";
}

export function TournamentsPage() {
  const state = useTournamentPreparation();
  const [creating, setCreating] = useState(false);
  const data = state.preparation.data;
  const tournaments = state.tournaments.data?.tournaments || [];
  const error = state.preparation.error || state.tournaments.error;
  const errorMessage = error instanceof ApiError ? error.message : error instanceof Error ? error.message : "";
  const selected = state.selectedTournament;

  if (!state.tournamentId) {
    return (
      <section className="page-grid prep-page tournament-page premium-tournament-catalog-page">
        <article className="tournament-catalog-command">
          <div>
            <span className="eyebrow">TOUR OPERATIONS</span>
            <h2>Turnieje</h2>
            <p>Najważniejsze wydarzenie jest zawsze pierwsze. Nadchodzące są pod ręką, a zakończone nie konkurują z bieżącą pracą.</p>
          </div>
          <div className="tournament-catalog-command-actions">
            <label><span>Sezon</span><select value={state.season} onChange={(event) => state.setSeason(Number(event.target.value))}>{[2026, 2025, 2024].map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
            <button type="button" className="primary-button" onClick={() => setCreating(true)}>+ Nowy turniej</button>
          </div>
        </article>

        {errorMessage ? <article className="panel prep-error"><h3>Nie udało się wczytać turniejów</h3><p>{errorMessage}</p></article> : null}
        <TournamentPicker tournaments={tournaments} season={state.season} loading={state.tournaments.isPending} onOpen={(id) => state.setTournamentId(id)} />

        {creating ? <NewTournamentDialog season={state.season} tournaments={tournaments} onClose={() => setCreating(false)} onCreated={(id) => { state.setTournamentId(id); setCreating(false); }} /> : null}
      </section>
    );
  }

  if (errorMessage) {
    return <section className="page-grid prep-page tournament-page"><article className="panel prep-error"><h3>Nie udało się wczytać turnieju</h3><p>{errorMessage}</p></article></section>;
  }

  if (!selected || state.preparation.isPending || !data) {
    return <section className="page-grid prep-page tournament-page"><article className="panel round-loading"><div className="round-loading-bar"/><span>Wczytuję pulpit turnieju…</span></article></section>;
  }

  const selectedStatus = effectiveTournamentStatus(selected);
  const currentWeek = tournamentInCurrentWeek(selected);
  const rooms = data.staff.filter((person) => Boolean(person.roomName)).length;
  const readiness = [
    { label: "Obsada", value: data.summary.staffCount, hint: `${data.summary.driverCount} kierowców` },
    { label: "Noclegi", value: data.summary.accommodationCount, hint: `${rooms} przypisanych pokoi` },
    { label: "Samochody", value: data.summary.vehicleCount, hint: "flota turnieju" },
    { label: "Bez TV", value: data.summary.tvExcludedCount, hint: "wykluczenia coverage" },
  ];

  return (
    <section className="page-grid prep-page tournament-page premium-tournament-dashboard">
      <article className="panel tournament-dashboard-overview">
        <header>
          <div>
            <span className="eyebrow">PULPIT OPERACYJNY</span>
            <h3>Gotowość turnieju</h3>
            <p>Najważniejsze liczby przed wejściem w rundy. Pełne listy pozostają w Obsada i Logistyka.</p>
          </div>
          <span className={`tournament-dashboard-status ${selectedStatus}`}>{currentWeek && selectedStatus !== "completed" ? "TEN TYDZIEŃ" : statusLabel(selectedStatus)}</span>
        </header>
        <div className="tournament-dashboard-metrics">
          {readiness.map((metric) => <div key={metric.label}><small>{metric.label}</small><strong>{metric.value}</strong><span>{metric.hint}</span></div>)}
        </div>
      </article>

      <div className="tournament-dashboard-grid">
        <article className="panel tournament-dashboard-details">
          <header>
            <div><span className="eyebrow">KONFIGURACJA</span><h3>Dane turnieju</h3></div>
            <div className="tournament-dashboard-detail-actions"><span className="prep-config-revision">CONFIG r{data.configRevision}</span><TournamentDetailsEditor tournamentId={state.tournamentId} data={data} /></div>
          </header>
          <div className="tournament-dashboard-facts">
            <div><small>Termin</small><strong>{dateRange(data.tournament.startDate, data.tournament.endDate)}</strong></div>
            <div><small>Lokalizacja</small><strong>{[data.tournament.location, data.tournament.country].filter(Boolean).join(" · ") || "Nieuzupełniona"}</strong></div>
            <div><small>Bufor przed startem</small><strong>{data.tournament.fieldBuffer} min</strong></div>
            <div><small>Tee Times</small><strong>{data.tournament.teeTimesUrl ? "Ręczny override" : "AUTO · DP World Tour"}</strong></div>
          </div>
          <div className="tournament-dashboard-note"><small>NOTATKA TURNIEJOWA</small><p>{data.tournament.notes || "Brak notatki."}</p></div>
        </article>

        <article className="panel tournament-dashboard-actions">
          <header><span className="eyebrow">NASTĘPNY KROK</span><h3>Pracuj na turnieju</h3><p>Wejdź bezpośrednio tam, gdzie teraz potrzebujesz działać.</p></header>
          <div>
            <Link to={`/tournaments?tab=team&tournament=${encodeURIComponent(state.tournamentId)}&season=${state.season}`}><span>Obsada</span><small>Ludzie, role i import</small><b>→</b></Link>
            <Link to={`/tournaments?tab=logistics&tournament=${encodeURIComponent(state.tournamentId)}&season=${state.season}`}><span>Logistyka</span><small>Noclegi, auta i czasy dojazdu</small><b>→</b></Link>
            <Link className="primary" to={`/generator?tournament=${encodeURIComponent(state.tournamentId)}&season=${state.season}`}><span>Generator</span><small>Rundy, transport i eksport</small><b>→</b></Link>
          </div>
        </article>
      </div>

      <article className="panel tournament-dashboard-rounds">
        <header><div><span className="eyebrow">RUNDY</span><h3>R1–R4</h3></div><p>Jedno kliknięcie otwiera Generator już w kontekście tego turnieju i wybranej rundy.</p></header>
        <div>{(["R1", "R2", "R3", "R4"] as const).map((round) => <Link to={`/generator?round=${round}&tournament=${encodeURIComponent(state.tournamentId)}&season=${state.season}`} key={round}><span>{round}</span><strong>{data.summary.roundScorerCounts[round]}</strong><small>scorerów</small><b>Otwórz →</b></Link>)}</div>
      </article>

      <details className="panel tournament-dashboard-advanced">
        <summary><div><span className="eyebrow">ADMINISTRACJA</span><strong>Zaawansowane operacje turnieju</strong><small>Rzadziej używane akcje są schowane, żeby nie przeszkadzały w codziennej pracy.</small></div><b>⌄</b></summary>
        <div><TournamentOperationsPanel tournamentId={state.tournamentId} tournamentName={data.tournament.name} season={data.tournament.season} onDeleted={() => state.setTournamentId("")} /></div>
      </details>
    </section>
  );
}
